import TextForm from '../../../components/TextForm/TextForm';
import PushTip from '../../../components/UI/PushTip/PushTip';
import { TelegramPreview, MAX_TEXT_LENGTH } from './PaymentTexts';

// Espelha src/telegram/presenters/local_templates.py (+ os botões de card_texts).
// Forma de pagamento nova = uma entrada aqui (vira mais uma aba).
export const METHODS = [
    {
        key: 'spei', label: 'SPEI', currency: 'MXN', flag: 'mx', title: 'Mensagem do SPEI (México)',
        when: 'Enviada no chat com a CLABE para o comprador transferir.',
        variables: ['plano', 'duration', 'valor', 'clabe', 'banco', 'beneficiario', 'referencia'],
        tip: <>A variável <strong>{'{clabe}'}</strong> é obrigatória: é para onde o comprador transfere.</>,
        buttons: [['spei_copy_clabe', 'Copiar CLABE'], ['spei_copy_amount', 'Copiar monto']],
        sample: {
            plano: 'VIP', duration: 'Mensual', valor: '$499.00 MXN', clabe: '<code>646180157000000004</code>',
            banco: 'STP (Sistema de Transferencia y Pagos)', beneficiario: 'XPag Pagamentos', referencia: 'REF123456',
        },
    },
    {
        key: 'nequi', label: 'Nequi', currency: 'COP', flag: 'co', title: 'Mensagem do Nequi (Colômbia)',
        when: 'Vai como legenda da foto do QR Code.',
        variables: ['plano', 'duration', 'valor', 'codigo', 'validade'],
        tip: <>Até 900 caracteres. <strong>{'{codigo}'}</strong> é opcional: o QR já tem o pagamento.</>,
        buttons: [['nequi_copy', 'Copiar código']],
        sample: { plano: 'VIP', duration: 'Mensual', valor: '$114.520 COP', codigo: '<code>000201010212…</code>', validade: '45' },
    },
];

// Telas "Escolha da moeda" e "Link de pagamento" com um modelo só, como o
// SPEI (espelha src/telegram/presenters/screen_templates.py). {moedas} vira
// uma linha por moeda; os botões de moeda são fixos (só aparecem na prévia).
const CURRENCY_TIP = <>Todas as variáveis são opcionais. <strong>{'{moedas}'}</strong> vira uma linha por moeda, com o preço em cada uma (2 ou mais).</>;
const SECURE_TIP = <>Todas as variáveis são opcionais. O botão abre a página de pagamento.</>;

export const SCREEN_TEMPLATES = {
    usd: {
        method: {
            key: 'method', when: 'Quando o bot oferece mais de uma forma de pagamento.',
            variables: ['plano', 'duration', 'preco', 'bump', 'total'],
            tip: <>Todas as variáveis são opcionais. A linha de <strong>{'{bump}'}</strong> só aparece quando o comprador aceita o order bump.</>,
            buttons: [['card_button', 'do cartão', { toggle: false }]],
            sample: { plano: 'VIP', duration: 'Monthly', preco: '$8.00', bump: '$2.00', total: '$10.00' },
        },
        currency: {
            key: 'currency', when: 'Quando o cartão aceita mais de uma moeda.',
            variables: ['plano', 'duration', 'moedas'], tip: CURRENCY_TIP, buttons: [],
            sample: { plano: 'VIP', duration: 'Monthly', moedas: '🇺🇸 USD — $10.00\n🇪🇺 EUR — €9.20' },
            fixedButtons: ['🇺🇸 Dollar (USD)', '🇪🇺 Euro (EUR)'],
            // Com "Botões com valor" ligado (payment_handler._simple_label).
            simpleButtons: ['🇺🇸 USD · $10.00', '🇪🇺 EUR · €9.20'],
        },
        secure: {
            key: 'secure', when: 'A mensagem com o botão que abre o checkout.',
            variables: ['plano', 'duration', 'valor'], tip: SECURE_TIP,
            buttons: [['pay_button', 'de pagar', { toggle: false }]],
            sample: { plano: 'VIP', duration: 'Monthly', valor: '$10.00' },
        },
    },
    mxn: {
        currency: {
            key: 'currency', when: 'Quando o bot aceita mais de uma moeda (ex.: MXN e COP).',
            variables: ['plano', 'duration', 'moedas'], tip: CURRENCY_TIP, buttons: [],
            sample: { plano: 'VIP', duration: 'Mensual', moedas: '🇲🇽 $499.00 MXN\n🇨🇴 $114.520 COP' },
            fixedButtons: ['🇲🇽 Peso mexicano (MXN)', '🇨🇴 Peso colombiano (COP)'],
            simpleButtons: ['🇲🇽 SPEI · MX$ 499.00', '🇨🇴 Nequi · COL$ 114.520'],
        },
        secure: {
            key: 'secure', when: 'Quando o pagamento abre na página de checkout (Chile, ou SPEI/Nequi configurados para a página).',
            variables: ['plano', 'duration', 'valor'], tip: SECURE_TIP,
            buttons: [['pay_button', 'de pagar', { toggle: false }]],
            sample: { plano: 'VIP', duration: 'Mensual', valor: '$499.00 MXN' },
        },
    },
};

const ALLOWED_TAGS = /&lt;(\/?)(b|strong|i|em|u|s|del|code|pre|blockquote)&gt;/g;

/** Mensagem com o exemplo no lugar das variáveis, só com as tags do Telegram. */
function previewHtml(template, sample) {
    const escaped = String(template || '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(ALLOWED_TAGS, '<$1$2>');
    return escaped.replace(/\{(\w+)\}/g, (all, name) => (name in sample ? sample[name] : all));
}

/** Botão com liga/desliga (se der para desligar) e texto (desligado = não vai no Telegram). */
function ButtonRow({ id, label, on, text, placeholder, onToggle, onText, toggle = true }) {
    return (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10">
            {toggle && <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input type="checkbox" role="switch" className="sr-only peer" aria-label={`Botão ${label}`}
                    checked={on} onChange={(e) => onToggle(id, e.target.checked)} />
                <div className="w-9 h-5 bg-gray-600 rounded-full peer peer-checked:after:translate-x-4 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500" />
            </label>}
            <input type="text" className="input3 disabled:opacity-40" aria-label={`Texto do botão ${label}`}
                maxLength={MAX_TEXT_LENGTH} disabled={!on} placeholder={placeholder}
                value={text || ''} onChange={(e) => onText(id, e.target.value)} />
        </div>
    );
}

/**
 * Uma mensagem com modelo (SPEI, Nequi, escolha da moeda, link de pagamento):
 * o texto livre (como o do PIX) e os botões à esquerda, a prévia à direita.
 * Linha com variável vazia (ex.: banco que não veio) some sozinha no Telegram.
 */
export default function MethodEditor({ method, template, defaultTemplate, onTemplateChange,
    texts, textDefaults, onTextsChange, buttonsOff = [], onButtonsOffChange = () => {}, simpleButtons = false }) {
    const off = new Set(buttonsOff);
    const textOf = (key) => (texts[key] && texts[key].trim()) || textDefaults[key] || '';
    const shownButtons = (simpleButtons && method.simpleButtons) || method.fixedButtons
        || method.buttons.filter(([key]) => !off.has(key)).map(([key]) => textOf(key));

    const toggle = (key, on) => {
        const next = new Set(off);
        if (on) next.delete(key); else next.add(key);
        onButtonsOffChange([...buttonsOff.filter((k) => next.has(k)), ...[...next].filter((k) => !buttonsOff.includes(k))]);
    };

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-3">
                <p className="spt-hint mb-0">{method.when}</p>
                {template !== defaultTemplate && (
                    <button type="button" className="shrink-0 text-[11px] text-white/50 hover:text-white underline"
                        onClick={() => onTemplateChange(defaultTemplate)}>
                        Voltar ao padrão
                    </button>
                )}
            </div>

            {/* Editor e prévia meio a meio no desktop (como todas as mensagens). */}
            <div data-testid="message-layout" className="grid gap-4 md:grid-cols-2 items-start">
                <div className="flex flex-col gap-3 min-w-0">
                    <TextForm key={method.key} value={template} onChange={onTemplateChange} variables={method.variables} />
                    <PushTip>{method.tip}</PushTip>
                    {method.buttons.length > 0 && (
                        <div className="flex flex-col gap-2">
                            <p className="text-[11px] text-white/40">Botões</p>
                            {method.buttons.map(([key, label, opts = {}]) => (
                                <ButtonRow key={key} id={key} label={label} on={!off.has(key)} text={texts[key]}
                                    placeholder={textDefaults[key]} onToggle={toggle} toggle={opts.toggle !== false}
                                    onText={(k, value) => onTextsChange({ ...texts, [k]: value })} />
                            ))}
                        </div>
                    )}
                </div>
                <div className="min-w-0 md:sticky md:top-4">
                    <p className="text-[11px] text-white/40 mb-1.5">Prévia</p>
                    <TelegramPreview html={previewHtml(template, method.sample)} buttons={shownButtons}
                        fixedButtons={Boolean(method.fixedButtons)} />
                </div>
            </div>
        </div>
    );
}
